import {
  RadioGroup as BaseRadioGroup,
  Field,
  type FieldRootProps,
  type RadioGroupRootProps,
} from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import {
  FormLabel,
  type FormLabelClassNames,
  type LabelOptionProps,
} from "../../shared/form-label/FormLabel.js";

type RadioDataProps =
  | string[]
  | {
      value: string;
      label: string;
      disabled?: boolean;
      supportingText?: string;
    }[];

interface RadioProps extends RadioGroupRootProps {
  data: RadioDataProps;
  label?: LabelOptionProps;
  invertColors?: boolean;
  error?: boolean;
  asterisk?: boolean;
  helperText?: HelperTextProps;
  fieldProps?: FieldRootProps;
  size?: "sm" | "md" | "lg";
  fontWeight?: "regular" | "medium" | "semibold" | "bold";
  classNames?: {
    root?: string;
    label?: string;
    list?: string;
    field?: string;
    item?: string;
    itemControl?: string;
    itemText?: string;
    helperText?: string;
  };
  labelClassNames?: FormLabelClassNames;
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof BaseRadioGroup.ItemHiddenInput>,
  RadioProps
>(
  (
    {
      className,
      data,
      label,
      orientation,
      asterisk = false,
      helperText = { message: "", status: "default" },
      classNames,
      fieldProps,
      error,
      size = "md",
      invertColors,
      labelClassNames,
      fontWeight = "regular",
      ...props
    },
    ref,
  ) => {
    return (
      <Field.Root
        {...fieldProps}
        invalid={error}
        className={clsx(fieldProps?.className, classNames?.field)}
      >
        <BaseRadioGroup.Root
          orientation={orientation}
          className={clsx(
            "ox-reset ox-RadioGroupRoot",
            className,
            classNames?.root,
          )}
          data-size={size}
          {...props}
        >
          {label ? (
            <FormLabel
              label={label}
              size={size}
              error={error}
              classNames={labelClassNames}
            />
          ) : null}

          <div
            data-orientation={orientation}
            className={clsx("ox-reset ox-RadioGroupList", classNames?.list)}
          >
            {data.map((item) => {
              if (typeof item === "string") {
                return (
                  <BaseRadioGroup.Item
                    className={clsx(
                      "ox-reset ox-RadioGroupItem",
                      classNames?.item,
                    )}
                    key={item}
                    value={item}
                  >
                    <BaseRadioGroup.ItemControl
                      className={clsx(
                        "ox-reset ox-RadioGroupItemControl",
                        invertColors && "inverted",
                        classNames?.itemControl,
                      )}
                    />
                    <BaseRadioGroup.ItemText
                      className={clsx(
                        "ox-reset ox-RadioGroupItemText",
                        invertColors && "inverted",
                        size === "sm"
                          ? "ox-fz-2xs"
                          : size === "md"
                            ? "ox-fz-sm"
                            : size === "lg"
                              ? "ox-fz-md"
                              : "ox-fz-sm",
                        fontWeight === "regular"
                          ? "ox-fw-regular"
                          : fontWeight === "medium"
                            ? "ox-fw-medium"
                            : fontWeight === "bold"
                              ? "ox-fw-bold"
                              : fontWeight === "semibold"
                                ? "ox-fw-semibold"
                                : "ox-fw-regular",
                        classNames?.itemText,
                      )}
                    >
                      {item}
                    </BaseRadioGroup.ItemText>

                    <BaseRadioGroup.ItemHiddenInput ref={ref} />
                  </BaseRadioGroup.Item>
                );
              } else {
                return (
                  <div key={item.value}>
                    <BaseRadioGroup.Item
                      disabled={item.disabled}
                      value={item.value}
                      className={clsx(
                        "ox-reset ox-RadioGroupItem",
                        classNames?.item,
                      )}
                    >
                      <BaseRadioGroup.ItemControl
                        className={clsx(
                          "ox-reset ox-RadioGroupItemControl",
                          classNames?.itemControl,
                        )}
                      />
                      <BaseRadioGroup.ItemText
                        className={clsx(
                          "ox-reset ox-RadioGroupItemText",
                          invertColors && "inverted",
                          size === "sm"
                            ? "ox-fz-2xs"
                            : size === "md"
                              ? "ox-fz-sm"
                              : size === "lg"
                                ? "ox-fz-md"
                                : "",
                          classNames?.itemText,
                        )}
                      >
                        {item.label}
                      </BaseRadioGroup.ItemText>

                      <BaseRadioGroup.ItemHiddenInput ref={ref} />
                    </BaseRadioGroup.Item>

                    {item?.supportingText ? (
                      <div
                        className="ox-reset ox-RadioGroupSupportingTextContainer"
                        data-size={size}
                      >
                        <p
                          className={clsx(
                            "ox-reset ox-RadioGroupSupportingText",
                          )}
                        >
                          {item?.supportingText}
                        </p>
                      </div>
                    ) : null}
                  </div>
                );
              }
            })}
          </div>
        </BaseRadioGroup.Root>
        {helperText?.message ? (
          <Field.HelperText
            data-size={size}
            data-status={helperText?.status}
            className={clsx(
              "ox-reset ox-FormHelperText",
              classNames?.helperText,
            )}
          >
            {getFormHelperText(helperText, size)}
          </Field.HelperText>
        ) : null}
      </Field.Root>
    );
  },
);

RadioGroup.displayName = "RadioGroup";
export { RadioGroup };
export type { RadioProps };
